import React from 'react';
import { motion } from 'framer-motion';
import { HeartHandshake, Brain, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { BRAND } from '../../data/brand';

export default function Method() {
  const { t, currentLanguage } = useTranslation();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  
  const features = [
    {
      icon: <HeartHandshake className="h-5 w-5" />,
      title: t('method.gentle'),
      text: t('method.gentle_desc'),
    },
    {
      icon: <Brain className="h-5 w-5" />,
      title: t('method.effective'),
      text: t('method.effective_desc'),
    },
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      title: t('method.safe'),
      text: t('method.safe_desc'),
    },
  ];

  const steps = [
    t('method.step1'),
    t('method.step2'),
    t('method.step3'),
    t('method.step4'),
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section id="method" className="py-16 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Левая колонка - описание */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <motion.h2 variants={item} className="text-2xl md:text-3xl font-semibold mb-4 text-white">
              {t('method.title')}
            </motion.h2>
            <motion.p variants={item} className="text-gray-300 mb-6 max-w-prose">
              {t('method.description')}
            </motion.p>
            
            {/* Преимущества */}
            <div className="grid sm:grid-cols-3 gap-4">
              {features.map((feature) => (
                <motion.div
                  key={feature.title}
                  variants={item}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-white/20 hover:bg-white/20 transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-rose-300">
                      {feature.icon}
                    </div>
                    <p className="font-medium text-white">{feature.title}</p>
                  </div>
                  <p className="text-sm text-gray-200">{feature.text}</p>
                </motion.div>
              ))}
            </div>

            {/* Гарантии и безопасность */}
            <motion.div variants={item} className="mt-6">
              <h3 className="text-lg font-semibold mb-4 text-white">{t('method.guarantees')}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-rose-300/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-rose-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="font-medium text-white">{t('method.privacy')}</p>
                  </div>
                  <p className="text-sm text-gray-200">— {t('method.privacy_desc')}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <p className="font-medium text-white">{t('method.control')}</p>
                  </div>
                  <p className="text-sm text-gray-200">— {t('method.control_desc')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Правая колонка - процесс и изображение */}
          <div className="space-y-6">
            {/* Изображение процесса */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-3xl shadow-lg overflow-hidden border border-white/20"
            >
              <img
                src="/images/Alfira-1.jpg"
                alt={t('method.process_image_alt')}
                className="w-full h-64 object-cover object-center"
              />
            </motion.div>
            
            {/* Карточка с процессом */}
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-3xl shadow-lg p-6 border border-white/20"
            >
              <motion.h3 variants={item} className="text-xl font-semibold mb-6 text-white">
                {t('method.typical_process')}
              </motion.h3>
              
              <div className="space-y-4 text-sm text-gray-200 mb-6">
                {steps.map((step, index) => (
                  <motion.div
                    key={step}
                    variants={item}
                    className="flex gap-3 items-start"
                  >
                    <div className="h-6 w-6 rounded-full bg-rose-300/20 flex items-center justify-center text-xs font-medium text-rose-300 flex-shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <p>{step}</p>
                  </motion.div>
                ))}
              </div>
              
              <motion.div variants={item}>
                <a
                  href={getLocalizedPath("/contact")}
                  className="bg-rose-300 hover:bg-rose-400 text-gray-800 font-semibold py-3 px-6 rounded-xl w-full text-center block transition-colors"
                >
                  {t('method.free_consultation')}
                </a>
                <p className="text-xs text-gray-400 text-center mt-2">
                  {t('method.consultation_desc')}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
